import { NgClass, UpperCasePipe } from '@angular/common';
import { Component, computed, OnInit, Signal, signal, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatChip } from '@angular/material/chips';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { EDialogWidth } from '../../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../../core/validators/trimmed-required.validator';
import { DatatableComponent } from '../../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../../shared/components/datatable/datatable.interface';
import { ErrorMessagesComponent } from '../../../../shared/components/error-messages/error-messages.component';
import { IDropdownOption, MatDropdown } from '../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { FormUtilitiesService } from '../../../../utilities/form-utilities.service';
import { HttpUtilitiesService } from '../../../../utilities/http-utilities.service';
import { BaseCrudPageComponent } from '../../../app/base-crud-page.component';
import { AI_INSTRUCTIONS_MAX_LENGTH } from '../ai-providers/ai-providers.interface';
import {
  IAiAgent,
  TAddAiAgent,
  TAiAgentRow,
  TAiProviderOption,
  TFormAiAgent,
  TFormControlAiAgent,
} from './ai-agents.interface';
import { AiAgentsStore, IAiAgentComponentState } from './ai-agents.store';

@Component({
  imports: [
    DatatableComponent,
    ErrorMessagesComponent,
    MatButton,
    MatCard,
    MatCardContent,
    MatCheckbox,
    MatChip,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatDropdown,
    MatError,
    MatFormField,
    MatHint,
    MatIcon,
    MatInput,
    MatLabel,
    NgClass,
    ReactiveFormsModule,
    TranslateModule,
    UpperCasePipe,
  ],
  providers: [AiAgentsStore, HttpUtilitiesService],
  selector: 'app-ai-agents',
  standalone: true,
  templateUrl: './ai-agents.component.html',
})
export class AiAgentsComponent extends BaseCrudPageComponent<IAiAgent, IAiAgentComponentState> implements OnInit {
  @ViewChild('addEditFormDialog') addEditFormDialog!: TemplateRef<HTMLElement>;

  public datatableConfigurations: IDatatableConfiguration<TAiAgentRow> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.bulk,
    editScope: ETableCrudScope.single,
  };

  public readonly datatableColumns: IDatatableColumn<TAiAgentRow>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
    { id: 'aiProvider', isSortable: false },
    { id: 'model', isSortable: true },
    { id: 'isActive', isSortable: true },
  ];

  public readonly addEditForm: FormGroup<TFormControlAiAgent> = new FormGroup({
    aiProviderId: new FormControl([] as IDropdownOption[], [Validators.required]),
    instructions: new FormControl('', [
      Validators.required,
      TrimmedRequiredValidator(),
      Validators.maxLength(AI_INSTRUCTIONS_MAX_LENGTH),
    ]),
    isActive: new FormControl(true),
    model: new FormControl([] as IDropdownOption<string>[], [Validators.required]),
    name: new FormControl('', [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)]),
  });

  public readonly providerOptions: Signal<IDropdownOption[]> = computed(() =>
    this.store.providers$().map((provider: TAiProviderOption) => ({ id: provider.id, name: provider.name })),
  );

  public readonly providerNameById: Signal<Record<number, string>> = computed(() =>
    Object.fromEntries(this.store.providers$().map((provider: TAiProviderOption) => [provider.id, provider.name])),
  );

  public readonly datatableRows: Signal<TAiAgentRow[]> = computed(() =>
    this.store
      .data$()
      .map((agent: IAiAgent) => ({ ...agent, aiProvider: this.providerNameById()[agent.aiProviderId] ?? '' })),
  );

  private readonly selectedProviderId = signal<number | null>(null);

  // Loading the providers replaces the rows and the datatable clears its checkboxes, so the edited agent is kept
  // here instead of read from `selectedRows()` again on submit.
  private editedAgentId: number | null = null;

  // Models of the selected provider's type, as configured on the API.
  public readonly modelOptions: Signal<IDropdownOption<string>[]> = computed(() => {
    const provider: TAiProviderOption | undefined = this.store
      .providers$()
      .find((option: TAiProviderOption) => option.id === this.selectedProviderId());
    const models: string[] = (provider && this.store.providerConfigs$()?.[provider.providerType]?.models) || [];

    return models.map((model: string) => ({ id: model, name: model }));
  });

  constructor(
    public readonly store: AiAgentsStore,
    public readonly formService: FormUtilitiesService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfAiAgent',
      multiContext: 'field.aiAgents',
      nameProperty: 'name',
      singleContext: 'field.aiAgent',
    });
  }

  public ngOnInit(): void {
    this.store.loadProviders();

    this.subscriptions.push(
      this.addEditForm.controls.aiProviderId.valueChanges.subscribe((value: IDropdownOption[] | null) => {
        const providerId: number | null = value?.[0]?.id ?? null;

        // Another provider may offer other models, so the model is picked again.
        if (providerId !== this.selectedProviderId()) {
          this.selectedProviderId.set(providerId);
          this.addEditForm.controls.model.setValue([]);
        }
      }),
    );

    this.subscriptions.push(
      this.datatableParams$.pipe(filter(Boolean)).subscribe((datatable) => {
        this.store.loadItems({
          fields: ['name', 'aiProviderId', 'model', 'instructions', 'isActive'],
          limit: datatable.limit,
          page: datatable.page,
          sort: [datatable.sort],
          ...(datatable.search ? { search: { searchedFields: ['name'], searchText: datatable.search } } : {}),
        });
      }),
    );
  }

  public initializeAdd(): void {
    // Providers may have been added on the Providers tab since this component loaded.
    this.store.loadProviders();
    this.editedAgentId = null;
    this.setupForAdd();
    this.addEditForm.enable();
    this.addEditForm.reset({ aiProviderId: [], instructions: '', isActive: true, model: [], name: '' });

    this.openFormDialog();
  }

  public initializeEdit(): void {
    const [item] = this.selectedRows();

    if (!item) {
      return;
    }

    this.store.loadProviders();
    this.editedAgentId = item.id;
    this.setupForEdit();
    this.addEditForm.enable();
    this.addEditForm.reset({
      aiProviderId: [{ id: item.aiProviderId, name: this.providerNameById()[item.aiProviderId] ?? '' }],
      instructions: item.instructions,
      isActive: item.isActive,
      model: [{ id: item.model, name: item.model }],
      name: item.name,
    });

    this.openFormDialog();
  }

  public onFormDialogSubmit(): void {
    if (!this.addEditForm.valid) {
      return;
    }

    const value: TFormAiAgent = this.addEditForm.getRawValue() as TFormAiAgent;
    const payload: TAddAiAgent = {
      aiProviderId: value.aiProviderId[0].id,
      instructions: FormUtilitiesService.trimIfString(value.instructions),
      isActive: value.isActive,
      model: value.model[0].id,
      name: FormUtilitiesService.trimIfString(value.name),
    };

    if (!this.isEditDialog()) {
      this.store.createOne(payload);
    } else if (this.editedAgentId !== null) {
      this.store.updateOne({ dto: payload, id: this.editedAgentId });
    }

    this.matDialog.closeAll();
  }

  private openFormDialog(): void {
    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.small,
    });
  }
}
